import { IRootScopeService, IPromise } from "angular";
import { IModalScope, IModalService } from "angular-ui-bootstrap";
import { IBadgeAssignment, IBadge } from "../models/badges";
import { BadgeService } from "../services/badges";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { AnnouncementService } from "@gtmhub/announcements/services/announcements.service";
import { UIErrorHandlingService, IUIError } from "@gtmhub/error-handling";
import { BadgeEvents } from "../badgeEvents";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { IGetAnnoncementsParams } from "@gtmhub/announcements/models/announcements";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";

interface IBadgeScope extends IModalScope {
  badgeAssignment: IBadgeAssignment;
  badge: IBadge;
  assigneesIdMap: IAssigneeIdMap;
  indicators: { approving: boolean; gettingBadgeAssignment: boolean };
  unassign();
  approveBadgeAward(badgeAssignmentRequest: IBadgeAssignment);
  rejectBadgeAward(badgeAssignmentRequest: IBadgeAssignment);
}

const bindStateToScope = (state: IAssigneesStoreState) => ({
  assigneesIdMap: state.assignees.map,
});

export class BadgeCtrl {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$uibModal",
    "BadgeService",
    "AnnouncementService",
    "UIErrorHandlingService",
    "AssigneeActions",
    "$ngRedux",
    "badgeAssignmentId",
  ];

  constructor(
    private $rootScope: IRootScopeService & ITraceRootScopeService,
    private $scope: IBadgeScope,
    private $uibModal: IModalService,
    private badgeService: BadgeService,
    private announcementService: AnnouncementService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux,
    private badgeAssignmentId: string
  ) {
    this.badgeEvents = new BadgeEvents(this.$scope);

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$scope.indicators = { approving: false, gettingBadgeAssignment: false };
    this.$scope.unassign = this.unassign;
    this.$scope.approveBadgeAward = this.approveBadgeAward;
    this.$scope.rejectBadgeAward = this.rejectBadgeAward;

    this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    return this.getBadgeAssignment();
  };

  private badgeEvents: BadgeEvents;

  private approveBadgeAward = (badgeAssignment: IBadgeAssignment) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/confirm-approve.jade"),
      windowClass: "small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.approve = () => {
            ctrl.$rootScope.traceAction("approve_badge_award", () => {
              $scope.indicators = { approving: true };
              ctrl.badgeService.approveBadgeAward(badgeAssignment.id).then(
                () => {
                  ctrl.badgeEvents.broadcastBadgeAwardChanged({ action: "assigned", assignment: badgeAssignment, badge: ctrl.$scope.badge });

                  const announcement = ctrl.badgeService.configureRewardAnnouncement(badgeAssignment);
                  ctrl.announcementService.post(announcement).then(
                    () => {
                      $scope.indicators.approving = false;
                    },
                    (error) => ctrl.uiErrorHandlingService.handleIndicator(error, { indicator: "approving" })
                  );
                  $uibModalInstance.close();
                  ctrl.getBadgeAssignment();
                },
                (error) => ctrl.uiErrorHandlingService.handleIndicator(error, { indicator: "approving" })
              );
            });
          };

          $scope.close = () => {
            $uibModalInstance.close();
          };
        },
      ],
    });
  };

  private rejectBadgeAward = (badgeAssignment: IBadgeAssignment) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/confirm-reject.jade"),
      windowClass: "small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.reject = () => {
            ctrl.$rootScope.traceAction("reject_badge_award", () => {
              $scope.indicators = { rejecting: true };
              ctrl.badgeService.rejectBadgeAward(badgeAssignment.id).then(
                () => {
                  $scope.indicators.rejecting = false;
                  ctrl.getBadgeAssignment();
                  $uibModalInstance.close();
                },
                (err) => ctrl.uiErrorHandlingService.handleIndicator(err, { indicator: "rejecting", inline: true })
              );
            });
          };

          $scope.close = () => {
            $uibModalInstance.close();
          };
        },
      ],
    });
  };

  private unassign = () => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$scope.$close();

    this.$uibModal.open({
      templateUrl: require("../views/confirm-unassign.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.close = () => {
            $uibModalInstance.close();
          };

          $scope.unassign = () => {
            ctrl.$rootScope.traceAction("unassign_badge_award", () => {
              $scope.indicators = { unassigning: true };
              ctrl.badgeService.unassignBadge(ctrl.badgeAssignmentId).then(
                () => {
                  // delete the announcements as well
                  $scope.indicators.unassigning = false;
                  ctrl.$rootScope.$broadcast("employeeChanged", true);
                  ctrl.$rootScope.$broadcast("employeesChanged", true);
                  ctrl.badgeEvents.broadcastBadgeAwardChanged({
                    action: "unassigned",
                    assignment: ctrl.$scope.badgeAssignment,
                    badge: ctrl.$scope.badge,
                  });
                  $scope.indicators.deletingAnnouncement = true;

                  const params: IGetAnnoncementsParams = {
                    skip: 0,
                    take: null,
                    authorId: ctrl.$scope.badgeAssignment.assignedById,
                    before: null,
                    after: null,
                    fields: "id,authorId,announcementTargetId",
                    announcementType: "Recognition",
                  };

                  ctrl.announcementService.get(params).then(
                    (announcements) => {
                      if (announcements.items.length === 0) {
                        $scope.indicators.deletingAnnouncement = false;
                        $uibModalInstance.close();
                        return;
                      }
                      for (let i = 0; i < announcements.items.length; i++) {
                        if (announcements.items[i].announcementTargetId === ctrl.$scope.badgeAssignment.id) {
                          ctrl.announcementService.delete(announcements.items[i].id).then(
                            () => {
                              $scope.indicators.deletingAnnouncement = false;
                              $uibModalInstance.close();
                            },
                            (err) => ctrl.uiErrorHandlingService.handleIndicator(err, { indicator: "deletingAnnouncement", inline: true })
                          );
                          break;
                        }
                      }
                    },
                    (err) => ctrl.uiErrorHandlingService.handleIndicator(err, { indicator: "deletingAnnouncement", inline: true })
                  );
                },
                (err) => ctrl.uiErrorHandlingService.handleIndicator(err, { indicator: "unassigning", inline: true })
              );
            });
          };
        },
      ],
    });
  };

  private getBadgeAssignment = (): IPromise<unknown> => {
    this.$scope.indicators.gettingBadgeAssignment = true;

    return this.badgeService.getBadgeAssignments([this.badgeAssignmentId], [], null).then(
      (assignments) => {
        if (assignments.totalCount === 0) {
          const error: IUIError = {
            body: "We cannot find the badge award.",
            showIntercomLink: true,
          };
          this.uiErrorHandlingService.handleIndicator(error, { indicator: "gettingBadgeAssignment" });
          return;
        }
        this.$scope.badgeAssignment = assignments.items[0];

        this.badgeService.getBadge(assignments.items[0].badgeId).then(
          (badge) => {
            this.$scope.badge = badge;
            this.$scope.indicators.gettingBadgeAssignment = false;
          },
          (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "gettingBadgeAssignment" })
        );
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "gettingBadgeAssignment" })
    );
  };
}
